// src/components/Propuestas/AprobacionPublica.js
import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Table, Alert, Spinner, Form, Row, Col, Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import Swal from 'sweetalert2';
import { obtenerPropuestaPorToken, aprobarPropuesta, rechazarPropuesta, solicitarCambiosPropuesta, subirArchivoPublicoCliente, eliminarArchivoStorage, eliminarArchivoPublicoCliente } from '../../api/common';
import SolicitudCambios from './SolicitudCambios';
import extraerNombreArchivo from './extraerNombreArchivoFirebase';
import { generarPDFPropuesta } from './PdfPropuesta';

const AprobacionPublica = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [propuesta, setPropuesta] = useState(null);
  console.log('propuesta:', propuesta);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [procesando, setProcesando] = useState(false);
  const [mostrarCambios, setMostrarCambios] = useState(false);

  // Para aprobar/rechazar
  const [nombrePersona, setNombrePersona] = useState('');
  const [errNombre, setErrNombre] = useState('');
  const [comentario, setComentario] = useState('');
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [showModalRechazo, setShowModalRechazo] = useState(false);
  const [accion, setAccion] = useState(null); // 'aprobar' o 'rechazar'
  const [subiendoDocCliente, setSubiendoDocCliente] = useState(false);
  const [obsExpanded, setObsExpanded] = useState(false);
  const OBS_LIMIT = 2000;
  console.log('Token recibido:', nombrePersona);

  useEffect(() => {
    cargarPropuesta();
  }, [token]);

  const cargarPropuesta = async () => {
    setLoading(true);
    try {
      const response = await obtenerPropuestaPorToken(token);
      console.log('Respuesta al obtener propuesta por token:', response);
      
      if (response.status === 200 && response.data.data) {
        setPropuesta(response.data.data);
      } else {
        setMensaje({ 
          tipo: 'danger', 
          texto: response.message || 'No se pudo cargar la propuesta' 
        });
      }
    } catch (error) {
      console.error('Error al cargar propuesta:', error);
      setMensaje({ tipo: 'danger', texto: 'Error de conexión' });
    } finally {
      setLoading(false);
    }
  };

  const handleAprobar = async () => {
    if (!nombrePersona.trim()) {
      setErrNombre('Por favor ingrese su nombre completo');
      return;
    }
    setErrNombre('');

    // Advertir si hay documentos ISO pendientes por adjuntar del cliente
    if (propuesta.fileDownload?.length > 0 && !propuesta.fileAdjuntos?.length) {
      const confirmarPendiente = await Swal.fire({
        title: 'Documentos pendientes',
        html: `<p>Esta propuesta incluye <strong>${propuesta.fileDownload.length} documento(s) ISO 17025</strong> para revisar y diligenciar.</p><p>¿Desea aprobar sin haberlos adjuntado?</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#198754',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Aprobar de todas formas',
        cancelButtonText: 'Volver y adjuntar'
      });
      if (!confirmarPendiente.isConfirmed) return;
    }

    const confirmacion = await Swal.fire({
      title: '¿Aprobar propuesta?',
      text: '¿Está seguro de aprobar esta propuesta comercial? Esta acción no se puede deshacer.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#198754',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, aprobar',
      cancelButtonText: 'Cancelar'
    });

    if (!confirmacion.isConfirmed) return;

    setProcesando(true);
    try {
      console.log('Aprobando propuesta con token:', token, 'y nombre:', nombrePersona);
      const response = await aprobarPropuesta(token, nombrePersona);
      
      if (response.status === 200) {
        setMensaje({ 
          tipo: 'success', 
          texto: '¡Propuesta aprobada exitosamente! Nos pondremos en contacto pronto.' 
        });
        setPropuesta({ ...propuesta, estado: 'aprobada' });
        setAccion('aprobada');
      } else {
        setMensaje({ tipo: 'danger', texto: response.message || 'Error al aprobar' });
      }
    } catch (error) {
      setMensaje({ tipo: 'danger', texto: 'Error al aprobar propuesta' });
    } finally {
      setProcesando(false);
    }
  };

  const handleRechazar = async () => {
    if (!nombrePersona.trim()) {
      setErrNombre('Por favor ingrese su nombre completo');
      setShowModalRechazo(false);
      return;
    }
    setErrNombre('');

    if (!motivoRechazo.trim()) {
      Swal.fire({ icon: 'warning', title: 'Motivo requerido', text: 'Por favor indique el motivo del rechazo.' });
      return;
    }

    setProcesando(true);
    try {
      const response = await rechazarPropuesta(token, motivoRechazo, nombrePersona);
      
      if (response.status === 200) {
        setMensaje({ 
          tipo: 'info', 
          texto: 'Propuesta rechazada. Gracias por su feedback.' 
        });
        setPropuesta({ ...propuesta, estado: 'rechazada' });
        setAccion('rechazada');
        setShowModalRechazo(false);
      } else {
        setMensaje({ tipo: 'danger', texto: response.message || 'Error al rechazar' });
      }
    } catch (error) {
      setMensaje({ tipo: 'danger', texto: 'Error al rechazar propuesta' });
    } finally {
      setProcesando(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Cargando propuesta...</p>
        </div>
      </div>
    );
  }

  if (!propuesta) {
    return (
      <div className="container mt-5">
        <Alert variant="danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {mensaje.texto || 'Propuesta no encontrada o link inválido'}
        </Alert>
      </div>
    );
  }

  const yaRespondida = ['aprobada', 'rechazada', 'cambios_solicitados'].includes(propuesta.estado) || accion;

  const handleSolicitarCambios = async (solicitudes) => {
    setProcesando(true);
    try {
      const response = await solicitarCambiosPropuesta(token, solicitudes);
      if (response.status === 200) {
        setMensaje({ tipo: 'success', texto: 'Solicitud de cambios enviada correctamente.' });
        setPropuesta({ ...propuesta, estado: 'cambios_solicitados' });
        setAccion('cambios_solicitados');
        setMostrarCambios(false);
      } else {
        setMensaje({ tipo: 'danger', texto: response.message || 'Error al solicitar cambios' });
      }
    } catch (error) {
      setMensaje({ tipo: 'danger', texto: 'Error al solicitar cambios' });
    } finally {
      setProcesando(false);
    }
  };

  const handleGenerarPDF = async () => {

  // SE VA A UTILIZAR PDFPROPUESTA.JS DEL FRONT 
    try {
      const resultado = await generarPDFPropuesta(propuesta);
      
    } catch (error) {
      console.log(error)
    }

};

  
  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          {mensaje.texto && (
            <Alert 
              variant={mensaje.tipo} 
              dismissible 
              onClose={() => setMensaje({ tipo: '', texto: '' })}
            >
              {mensaje.texto}
            </Alert>
          )}

          <Card className="shadow w-100"> 
            <Card.Header className="bg-primary text-white text-center py-4 w-100">
              <h3 className="mb-0">PROPUESTA COMERCIAL</h3>
              <h5 className="mb-0 mt-2">{propuesta.consecutivo} - {propuesta.numero_version || 1}</h5>
            </Card.Header>

            <Card.Body className="p-4 w-100">
              {/* EMPRESA */}
              <div className="text-center mb-4">
                <h4>BIOLAB LABORATORIO METROLÓGICO S.A.S.</h4>
                <p className="mb-0">NIT 900.419.208-1</p>
                <p className="mb-0">CALLE 9 #16-09 - Cereté, Colombia</p>
                <p className="mb-0">Tel: 3215873582</p>
              </div>

              <hr />

              {/* INFORMACIÓN DEL CLIENTE */}
              <Row className="mb-4">
                <Col md={6}>
                  <h6 className="text-primary">CLIENTE</h6>
                  <table className="table table-sm table-borderless">
                    <tbody>
                      <tr>
                        <td width="30%" className="text-muted">Nombre:</td>
                        <td><strong>{propuesta.cliente_nombre}</strong></td>
                      </tr>
                      <tr>
                        <td className="text-muted">NIT:</td>
                        <td>{propuesta.cliente_nit}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Dirección:</td>
                        <td>{propuesta.cliente_direccion || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
                <Col md={6}>
                  <h6 className="text-primary">PROPUESTA</h6>
                  <table className="table table-sm table-borderless">
                    <tbody>
                      <tr>
                        <td width="30%" className="text-muted">Fecha:</td>
                        <td>{moment(propuesta.fecha_propuesta).format('DD/MM/YYYY')}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Vigencia:</td>
                        <td>{moment(propuesta.fecha_vigencia).format('DD/MM/YYYY')}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Estado:</td>
                        <td>
                          {propuesta.estado === 'aprobada' && <Badge bg="success">Aprobada</Badge>}
                          {propuesta.estado === 'rechazada' && <Badge bg="danger">Rechazada</Badge>}
                          {['enviada', 'vista'].includes(propuesta.estado) && <Badge bg="info">Pendiente de Respuesta</Badge>}
                          {propuesta.estado === 'cambios_solicitados' && <Badge bg="warning" text="dark">Cambios Solicitados</Badge>}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
              </Row>

              {/* ÍTEMS */}
              <h6 className="text-primary mb-3">DETALLE DE SERVICIOS</h6>
              <div className="table-responsive">
                <Table bordered>
                  <thead className="table-light">
                    <tr>
                      <th width="5%">#</th>
                      <th width="10%">Código</th>
                      <th width="25%">Nombre</th>
                      <th width="30%">Descripción</th>
                      <th width="8%" className="text-center">Cant.</th>
                      <th width="12%" className="text-end">V. Unit.</th>
                      <th width="8%" className="text-center">Dto. %</th>
                      <th width="10%" className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {propuesta.items?.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.codigo}</td>
                        <td><strong>{item.nombre}</strong></td>
                        <td><small>{item.descripcion}</small></td>
                        <td className="text-center">{item.cantidad}</td>
                        <td className="text-end">
                          ${item.valor_unitario.toLocaleString('es-CO')}
                        </td>
                        <td className="text-center">{item.taxDescuento > 0 ? `${item.taxDescuento}%` : '-'}</td>
                        <td className="text-end fw-bold">
                          ${item.total.toLocaleString('es-CO')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="7" className="text-end">
                        <strong>Total Bruto:</strong>
                      </td>
                      <td className="text-end">
                        ${propuesta.total_bruto?.toLocaleString('es-CO')}
                      </td>
                    </tr>
                    {(propuesta.total_bruto - propuesta.subtotal) > 0 && (
                      <tr className="table-warning">
                        <td colSpan="7" className="text-end">
                          <strong>Descuento:</strong>
                        </td>
                        <td className="text-end">
                          -${(propuesta.total_bruto - propuesta.subtotal)?.toLocaleString('es-CO')}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="7" className="text-end">
                        <strong>Subtotal:</strong>
                      </td>
                      <td className="text-end">
                        ${propuesta.subtotal?.toLocaleString('es-CO')}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="7" className="text-end">
                        <strong>IVA 19%:</strong>
                      </td>
                      <td className="text-end">
                        ${propuesta.total_iva?.toLocaleString('es-CO')}
                      </td>
                    </tr>
                    <tr className="table-primary">
                      <td colSpan="7" className="text-end">
                        <strong className="fs-5">TOTAL A PAGAR:</strong>
                      </td>
                      <td className="text-end">
                        <strong className="fs-5">
                          ${propuesta.total_pagar?.toLocaleString('es-CO')}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </div>

              {/* CONDICIONES COMERCIALES */}
              <div className="mt-4">
                <h6 className="text-primary">CONDICIONES COMERCIALES</h6>
                <div className="bg-light p-3 rounded">
                  <p className="mb-2">
                    <strong>Forma de Pago:</strong> {propuesta.condiciones_pago}
                  </p>
                  <p className="mb-0">
                    <strong>Tiempo de Entrega:</strong> {propuesta.tiempo_entrega}
                  </p>
                </div>
              </div>

              {propuesta.terminos_condiciones && (
                <div className="mt-3">
                  <h6 className="text-primary">TÉRMINOS Y CONDICIONES</h6>
                  <div className="bg-light p-3 rounded">
                    <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                      {propuesta.terminos_condiciones}
                    </p>
                  </div>
                </div>
              )}

              {/* OBSERVACIONES Y ARCHIVOS ADJUNTOS */}
              {propuesta.observaciones && ( 
                <Card className="border-0 bg-light mt-3 w-100">
                  <Card.Body className="w-100">
                    <h6 className="text-muted "> <strong>Observaciones</strong></h6>
                    <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                      {!obsExpanded && propuesta.observaciones.length > OBS_LIMIT
                        ? propuesta.observaciones.substring(0, OBS_LIMIT) + '…'
                        : propuesta.observaciones}
                    </p>
                    {propuesta.observaciones.length > OBS_LIMIT && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 mt-1"
                        onClick={() => setObsExpanded(v => !v)}
                      >
                        {obsExpanded
                          ? 'Ver menos'
                          : `Ver más (${(propuesta.observaciones.length - OBS_LIMIT).toLocaleString('es-CO')} caracteres más)`}
                      </Button>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* DOCUMENTOS ISO PARA DESCARGA */}
              {propuesta.fileDownload?.length > 0 && (
                <Alert variant="warning" className="mt-3">
                  <Alert.Heading><i className="bi bi-download me-2"></i>Documentos requeridos </Alert.Heading>
                  <p>Por favor descargue, lea y diligencia los siguientes documentos antes de aprobar la propuesta:</p>
                  <ul className="mb-2 list-unstyled ms-3 fw-bold">
                    {propuesta.fileDownload.map((url, i) => {
                      const nombre = extraerNombreArchivo(url) || `Documento ${i + 1}`;
                      return (
                        <li key={i} className="d-flex align-items-center mb-1">
                          <a href={url} target="_blank" rel="noreferrer">
                            <i className="bi bi-file-earmark-arrow-down me-1"></i>{nombre}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                  <hr />
                  <p className="mb-1 fw-bold">Adjunte los documentos diligenciados:</p>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,.xlsx,.xls,.doc,.docx"
                      disabled={subiendoDocCliente}
                      style={{ maxWidth: 340 }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setSubiendoDocCliente(true);
                        try {
                          await subirArchivoPublicoCliente(propuesta.token_aprobacion, file);
                          // Recargar propuesta para reflejar el nuevo adjunto
                          const res = await obtenerPropuestaPorToken(token);
                          if (res.data?.data) setPropuesta(res.data.data);
                        } catch { /* silencioso */ }
                        setSubiendoDocCliente(false);
                        e.target.value = '';
                      }}
                    />
                    {subiendoDocCliente && <Spinner animation="border" size="sm" />}
                  </div>
                  {propuesta.fileAdjuntos?.length > 0 && ( // mostrar los archivos y con opcion de borrar cada uno con un icono si el cliente se equivoco al subirlo, al eliminarlo se borra de firebase y se actualiza la propuesta para que ya no aparezca en el listado de adjuntos del cliente
                    <div className="mt-3">
                      <h6 className="mb-2">Documentos adjuntados:</h6>
                      <ul className="mb-0">
                        {propuesta.fileAdjuntos.map((url, i) => {
                          const nombre = extraerNombreArchivo(url) || `Documento ${i + 1}`;
                          return (
                            
                            <li key={i} className="d-flex align-items-center">
                              <a href={url} target="_blank" rel="noreferrer">
                                <i className="bi bi-file-earmark-arrow-down me-1"></i>{nombre}
                              </a>
                              <Button // boton transparente y text rojo para eliminar el archivo adjuntado por el cliente
                                variant="outline-danger"
                                size="sm"
                                className="ms-2 p-1 d-flex align-items-center justify-content-center"
                                onClick={async () => {
                                  try {
                                    //activar modal de confirmacion antes de eliminar el archivo
                                    const confirmacion = await Swal.fire({
                                      title: '¿Eliminar documento?',
                                      text: '¿Está seguro de eliminar este documento adjuntado? Esta acción no se puede deshacer.',
                                      icon: 'warning',
                                      showCancelButton: true, 
                                      confirmButtonColor: '#dc3545',
                                      cancelButtonColor: '#6c757d',
                                      confirmButtonText: 'Sí, eliminar',
                                      cancelButtonText: 'Cancelar'
                                    });
                                    if (!confirmacion.isConfirmed) return;  
                                    await eliminarArchivoPublicoCliente(propuesta.token_aprobacion, url);
                                    // Recargar propuesta para reflejar el cambio
                                    const res = await obtenerPropuestaPorToken(token);
                                    if (res.data?.data) setPropuesta(res.data.data);
                                  } catch { /* silencioso */ }
                                }}
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                  )}
                </Alert>
              )}

              {/* SECCIÓN DE APROBACIÓN/RECHAZO */}
              {!yaRespondida && (
                <>
                  <hr className="my-4" />
                  <div className="bg-light p-4 rounded">
                    <h5 className="text-center mb-4">
                      <i className="bi bi-hand-thumbs-up me-2"></i>
                      Responder a esta Propuesta
                    </h5>

                    <Row>
                      <Col md={6} className="mx-auto">
                        <Form.Group className="mb-3">
                          <Form.Label>Su Nombre Completo *</Form.Label>
                          <Form.Control
                            type="text"
                            value={nombrePersona}
                            onChange={(e) => { setNombrePersona(e.target.value); setErrNombre(''); }}
                            placeholder="Nombre de quien aprueba/rechaza"
                            isInvalid={!!errNombre}
                          />
                          {errNombre && <Form.Control.Feedback type="invalid">{errNombre}</Form.Control.Feedback>}
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>Comentario</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={comentario}
                            onChange={(e) => setComentario(e.target.value)}
                            placeholder="Comentario sobre la propuesta"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={3} className="mb-3">
                        <div className="d-grid">
                          <Button
                            variant="success"
                            size="lg"
                            onClick={handleAprobar}
                            disabled={procesando}
                          >
                            {procesando ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Procesando...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-check-circle me-2"></i>
                                APROBAR PROPUESTA
                              </>
                            )}
                          </Button>
                        </div>
                      </Col>

                      <Col md={3}>
                        <div className="d-grid">
                          <Button
                            variant="danger"
                            size="lg"
                            onClick={() => setShowModalRechazo(true)}
                            disabled={procesando}
                          >
                            {procesando ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Procesando...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-x-circle me-2"></i>
                                RECHAZAR
                              </>
                            )}
                          </Button>
                        </div>
                      </Col>

                      <Col md={3}>
                        <div className="d-grid">
                          <Button
                            variant="warning"
                            size="lg"
                            onClick={() => setMostrarCambios(!mostrarCambios)}
                            disabled={procesando}
                          >
                            <i className="bi bi-pencil-square me-2"></i>
                            SOLICITAR CAMBIOS
                          </Button>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="d-grid">
                          <Button 
                            variant="light" 
                            size="lg" 
                            onClick={handleGenerarPDF}>
                            <i className="bi bi-file-pdf me-1"></i>Exportar PDF
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    {mostrarCambios && (
                      <SolicitudCambios
                        items={propuesta.items || []}
                        onEnviar={handleSolicitarCambios}
                        loading={procesando}
                      />
                    )}

                    <Alert variant="info" className="mt-3 mb-0">
                      <i className="bi bi-info-circle me-2"></i>
                      Al aprobar o rechazar, se notificará automáticamente a nuestra empresa.
                    </Alert>
                  </div>
                </>
              )}

              {yaRespondida && (
                <Alert variant={
                  (accion === 'aprobada' || propuesta.estado === 'aprobada') ? 'success' :
                  (accion === 'cambios_solicitados' || propuesta.estado === 'cambios_solicitados') ? 'warning' : 'danger'
                } className="mt-4">
                  <h5>
                    <i className={`bi bi-${
                      (accion === 'aprobada' || propuesta.estado === 'aprobada') ? 'check-circle' :
                      (accion === 'cambios_solicitados' || propuesta.estado === 'cambios_solicitados') ? 'pencil-square' : 'x-circle'
                    } me-2`}></i>
                    {(accion === 'aprobada' || propuesta.estado === 'aprobada')
                      ? '¡Gracias por aprobar nuestra propuesta!'
                      : (accion === 'cambios_solicitados' || propuesta.estado === 'cambios_solicitados')
                        ? 'Solicitud de cambios enviada'
                        : 'Propuesta rechazada'}
                  </h5>
                  <p className="mb-0">
                    {(accion === 'aprobada' || propuesta.estado === 'aprobada')
                      ? 'Nos pondremos en contacto con usted a la brevedad para coordinar los siguientes pasos.'
                      : (accion === 'cambios_solicitados' || propuesta.estado === 'cambios_solicitados')
                        ? 'Hemos recibido su solicitud de cambios. Nuestro equipo comercial la revisará y le enviará una propuesta actualizada.'
                        : 'Agradecemos su tiempo y consideración. Quedamos a su disposición para futuras oportunidades.'}
                  </p>
                </Alert>
              )}
            </Card.Body>

            <Card.Footer className="text-center text-muted py-3 w-100">
              <small>
                Este documento es una propuesta comercial válida hasta el{' '}
                {moment(propuesta.fecha_vigencia).format('DD/MM/YYYY')}
              </small>
            </Card.Footer>
          </Card>
        </div>
      </div>
      {/* MODAL RECHAZO */}
      <Modal show={showModalRechazo} onHide={() => setShowModalRechazo(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-x-circle text-danger me-2"></i>
            Rechazar Propuesta
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">Por favor indique el motivo del rechazo para que podamos mejorar nuestro servicio.</p>
          <Form.Group>
            <Form.Label>Motivo del rechazo <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={motivoRechazo}
              onChange={(e) => setMotivoRechazo(e.target.value)}
              placeholder="Describa el motivo por el cual rechaza la propuesta..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalRechazo(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleRechazar} disabled={procesando}>
            {procesando ? (
              <><Spinner animation="border" size="sm" className="me-2" />Procesando...</>
            ) : (
              <><i className="bi bi-x-circle me-2"></i>Confirmar Rechazo</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AprobacionPublica;